import { useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import Button from '../ui/Button';
import { HERO, APP_STORE_URL } from '../../utils/constants';
import { spring, splitWords } from '../../utils/animations';

/* ── Canvas waveform ── GPU-composited, 60fps, no DOM nodes ── */
function WaveformCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const prefersReduced = useReducedMotion();

  const draw = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number, t: number) => {
    ctx.clearRect(0, 0, w, h);
    const barW = 2;
    const gap = 8;
    const bars = Math.floor(w / (barW + gap));
    const cx = bars / 2;
    const totalW = bars * (barW + gap) - gap;
    const offsetX = (w - totalW) / 2;

    for (let i = 0; i < bars; i++) {
      const dist = Math.abs(i - cx) / cx;
      // Smooth bell-curve falloff — visible at edges, stronger at center
      const envelope = Math.exp(-dist * dist * 2.5) * 0.85 + 0.1;
      const wave = Math.sin(i * 0.3 + t * 1.2) * 0.25
                 + Math.sin(i * 0.15 + t * 0.7) * 0.15
                 + 0.6;
      const barH = envelope * wave * h * 0.5;

      const x = offsetX + i * (barW + gap);
      const y = (h - barH) / 2;

      const alpha = envelope * 0.55;
      const grad = ctx.createLinearGradient(x, y, x, y + barH);
      grad.addColorStop(0, `rgba(59,130,246,${alpha * 0.5})`);
      grad.addColorStop(0.5, `rgba(139,92,246,${alpha})`);
      grad.addColorStop(1, `rgba(59,130,246,${alpha * 0.25})`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, y, barW, barH, 1);
      ctx.fill();
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReduced) return;
    const ctx = canvas.getContext('2d')!;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });

    let start = 0;
    const loop = (ts: number) => {
      if (!start) start = ts;
      const t = (ts - start) / 1000;
      const rect = canvas.getBoundingClientRect();
      draw(ctx, rect.width, rect.height, t);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [draw, prefersReduced]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
    />
  );
}

/* ── Word-by-word reveal ── */
function RevealHeadline({ text }: { text: string }) {
  const words = useMemo(() => splitWords(text), [text]);

  return (
    <h1 className="text-4xl sm:text-7xl lg:text-8xl font-bold leading-[1.4] tracking-tight">
      {words.map((word, i) => (
        <motion.span
          key={i}
          className="inline-block bg-gradient-brand bg-clip-text text-transparent mr-[0.27em] last:mr-0 pb-[0.15em]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            ...spring,
            delay: 0.3 + i * 0.07,
          }}
        >
          {word}
        </motion.span>
      ))}
    </h1>
  );
}

/* ── Hero section ── */
export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Parallax: content fades out and floats up as you scroll away
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden noise-texture isolate">
      {/* Static backgrounds — z-0, behind content */}
      <div className="absolute inset-0 z-0 grid-pattern" />
      <div className="absolute inset-0 z-[1]"><WaveformCanvas /></div>

      {/* Radial glows — z-[2], behind content */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-radial-purple opacity-25 gpu z-[2]" />
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] bg-gradient-radial-blue opacity-15 gpu z-[2]" />

      <motion.div
        className="container-custom relative z-10 pt-24 pb-20"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <div className="max-w-4xl mx-auto text-center space-y-10">
          <RevealHeadline text={HERO.headline.replace('\n', ' ')} />

          {/* Subheadline */}
          <motion.p
            className="text-xl sm:text-2xl text-white/60 max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.9 }}
          >
            {HERO.subheadline}
          </motion.p>

          {/* CTA */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 1.1 }}
          >
            <Button variant="cta" size="lg" href={APP_STORE_URL}>
              {HERO.cta}
            </Button>
            <span className="text-sm text-white/30">iOS &middot; Free to start</span>
          </motion.div>

          {/* Social proof */}
          <motion.div
            className="flex items-center justify-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
          >
            <div className="flex -space-x-2">
              {['#3B82F6', '#8B5CF6', '#06B6D4', '#10B981'].map((color, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-surface flex items-center justify-center text-xs font-bold text-white gpu"
                  style={{ background: color }}
                >
                  {String.fromCharCode(65 + i)}
                </div>
              ))}
            </div>
            <p className="text-sm text-white/40">{HERO.socialProof}</p>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w-5 h-8 rounded-full border border-white/15 flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/30 rounded-full" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
